<template>
  <f7-app v-bind="f7params">
    <template v-if="isCoreAppLoaded">
      <template v-if="passwordResetPending">
        <f7-login-screen :opened="true">
          <f7-view>
            <ResetPasswordPage @done="onPasswordResetDone" />
          </f7-view>
        </f7-login-screen>
      </template>

      <template v-else-if="isUserLoggedIn">
        <f7-views tabs class="safe-areas">
          <f7-toolbar tabbar icons bottom>
            <f7-toolbar-pane>
              <f7-link
                tab-link="#view-home"
                tab-link-active
                icon-ios="f7:house_fill"
                icon-md="material:home"
                text="Domov"
              ></f7-link>
              <f7-link
                tab-link="#view-profile"
                icon-ios="f7:person_crop_circle"
                icon-md="material:account_circle"
                text="Profil"
              ></f7-link>
            </f7-toolbar-pane>
          </f7-toolbar>

          <f7-view id="view-home" main tab tab-active url="/"></f7-view>
          <f7-view id="view-profile" name="profile" tab url="/profile/"></f7-view>
        </f7-views>

        <ParkingReservationForm class="parking-reservation-form" />
        <ParkingVehicleForm class="parking-vehicle-form" />
      </template>

      <template v-else>
        <f7-login-screen :opened="true">
          <f7-view>
            <LoginPage
              v-if="authView === 'login'"
              @forgot-password="authView = 'forgot-password'"
            />
            <ForgotPasswordPage
              v-else-if="authView === 'forgot-password'"
              @back="authView = 'login'"
            />
          </f7-view>
        </f7-login-screen>
      </template>
    </template>
  </f7-app>
</template>

<script setup lang="ts">
import { App, URLOpenListenerEvent } from '@capacitor/app'
import { Browser } from '@capacitor/browser'
import { Preferences } from '@capacitor/preferences'
import { SplashScreen } from '@capacitor/splash-screen'
import { inject } from '@vercel/analytics'
import { getDevice } from 'framework7'
import { f7, f7ready, useStore } from 'framework7-vue'
import { Settings } from 'luxon'
import { computed, onMounted, ref, watch } from 'vue'

import {
  AUTH_CALLBACK_PATH,
  checkTokenValidity,
  logoutWithoutPrompt,
  recheckForUpdate,
  RESET_PASSWORD_PATH,
  setActiveUserIfAny,
  showAuthenticationFailure,
} from '@/assets/composables/auth'
import { authFailureContext } from '@/assets/composables/authDiagnostics'
import { refreshBanStatus } from '@/assets/composables/ban'
import { enableRuntimeDiagnostics, showFailure } from '@/assets/composables/diagnostics'
import { isAmbiguousAuthCallback } from '@/assets/composables/errorPolicy'
import { showPreloader } from '@/assets/composables/preloader'
import { supabase } from '@/assets/composables/supabase'
import { checkForUpdate } from '@/assets/composables/update'
import {
  initNetworkListeners,
  initPushNotifications,
  syncDeviceToken,
} from '@/assets/composables/utils'
import ForgotPasswordPage from '@/components/forgot-password.vue'
import LoginPage from '@/components/login.vue'
import ParkingReservationForm from '@/components/parking/reservationForm/index.vue'
import ParkingVehicleForm from '@/components/parking/vehicleForm/index.vue'
import ResetPasswordPage from '@/components/reset-password.vue'
import capacitorApp from '@/js/capacitor-app.js'
import routes from '@/js/routes'
import store from '@/js/store'

Settings.defaultZone = 'Europe/Bratislava'
Settings.defaultLocale = 'sk-SK'

type AuthView = 'login' | 'forgot-password'

const device = getDevice()
const isCoreAppLoaded = ref<boolean | undefined>(undefined)
const isUserLoggedIn = computed<boolean>(() => useStore('isUserLoggedIn').value)
const authView = ref<AuthView>('login')

const passwordResetPending = ref<boolean>(false)
const PASSWORD_RESET_PENDING_KEY = 'passwordResetPending'

const setPasswordResetPending = async () => {
  passwordResetPending.value = true
  await Preferences.set({ key: PASSWORD_RESET_PENDING_KEY, value: '1' })
}

const f7params = {
  name: 'Pelikánec',
  theme: 'auto',
  colors: {
    primary: '#EC8023',
    secondary: '#42B769',
  },
  dialog: {
    buttonCancel: 'Zrušiť',
  },
  store: store,
  routes: routes,

  serviceWorker: import.meta.env.PROD
    ? {
        path: '/service-worker.js',
      }
    : {},

  input: {
    scrollIntoViewOnFocus: device.capacitor,
    scrollIntoViewCentered: device.capacitor,
  },

  statusbar: {
    iosOverlaysWebView: true,
    androidOverlaysWebView: true,
  },
}

const onPasswordResetDone = async () => {
  await logoutWithoutPrompt()
  passwordResetPending.value = false
  authView.value = 'login'
  f7.preloader.hide()
  f7.dialog.alert('Heslo bolo úspešne zmenené. Prihláste sa s novým heslom.', 'Heslo zmenené')
}

let isInitialized = false
let isInitAppRunning = false
let splashFallback: ReturnType<typeof setTimeout> | undefined
let closeAppPreloader: ReturnType<typeof showPreloader> | undefined

const initOnce = () => {
  if (isInitialized) return
  isInitialized = true

  initNetworkListeners()
  void initPushNotifications()

  if (!device.cordova) {
    inject()
  }
}

const initApp = async () => {
  if (isInitAppRunning) return
  isInitAppRunning = true

  initOnce()

  if (passwordResetPending.value) {
    isCoreAppLoaded.value = true
    isInitAppRunning = false
    clearTimeout(splashFallback)
    SplashScreen.hide()
    return
  }

  isCoreAppLoaded.value = false

  try {
    await setActiveUserIfAny()

    closeAppPreloader?.(false)
    closeAppPreloader = undefined
    const updateRequired = await checkForUpdate()

    if (!updateRequired) {
      isCoreAppLoaded.value = true
    }
  } finally {
    closeAppPreloader?.(false)
    closeAppPreloader = undefined
    isInitAppRunning = false
    clearTimeout(splashFallback)
    SplashScreen.hide()
  }
}

onMounted(() => {
  // Safety net
  if (device.capacitor) {
    splashFallback = setTimeout(() => SplashScreen.hide(), 10_000)
  }

  f7ready(async (f7) => {
    enableRuntimeDiagnostics()
    if (device.capacitor) {
      capacitorApp.init(f7)

      App.addListener('appUrlOpen', async (data: URLOpenListenerEvent) => {
        const url = new URL(data.url)
        const code = url.searchParams.get('code')

        const matchesPath = (path: string) => url.pathname.includes(path) || data.url.includes(path)

        if (matchesPath(RESET_PASSWORD_PATH) && code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code)
          if (error) {
            const existing = isAmbiguousAuthCallback(error)
              ? await supabase.auth.getSession().catch(() => ({ data: { session: null } }))
              : { data: { session: null } }
            await showFailure(error, {
              area: 'auth',
              operation: 'password-reset-callback',
              endpoint: '/auth/v1/token',
              policy: authFailureContext(existing.data.session),
              title: 'Obnovenie hesla sa nepodarilo',
              message:
                'Odkaz na obnovenie hesla sa nepodarilo použiť. Skúste to znova alebo si vyžiadajte nový odkaz.',
            })
          } else {
            await setPasswordResetPending()
          }
          try {
            await Browser.close()
          } catch {}
          isCoreAppLoaded.value = true
          return
        }

        if (matchesPath(AUTH_CALLBACK_PATH) && code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code)
          if (error) {
            await showAuthenticationFailure('callback', error)
          }
        }

        try {
          await Browser.close()
        } catch {}
        await initApp()
      })

      App.addListener('backButton', ({ canGoBack }) => {
        if (!canGoBack && !isUserLoggedIn.value && !passwordResetPending.value) {
          App.exitApp()
        } else if (canGoBack) {
          window.history.back()
        }
      })

      // This needs to be called due to the Android background app handling
      App.addListener('resume', async () => {
        // The app may be backgrounded for a long time, during which the ban and
        // unreturned-card state can change server-side. Both home banners are
        // otherwise only refreshed on page:init, so refresh them on resume too.
        if (isUserLoggedIn.value) {
          refreshBanStatus()
          const { default: Reservations } = await import('@/assets/classes/parking/Reservations')
          Reservations.fetchNewData()
        }
        await recheckForUpdate(checkForUpdate)
      })
    }

    watch(
      isUserLoggedIn,
      (loggedIn) => {
        if (loggedIn) {
          startTokenValidityCheck()
          syncDeviceToken()
        } else {
          stopTokenValidityCheck()
        }
      },
      { immediate: true },
    )

    const { value: pendingReset } = await Preferences.get({ key: PASSWORD_RESET_PENDING_KEY })
    if (pendingReset) {
      const {
        data: { session },
      } = await supabase.auth.getSession()
      if (session) {
        passwordResetPending.value = true
      } else {
        await Preferences.remove({ key: PASSWORD_RESET_PENDING_KEY })
      }
    }

    if (window.location.pathname.includes(RESET_PASSWORD_PATH)) {
      if (new URL(window.location.href).searchParams.has('code')) {
        const {
          data: { session },
        } = await supabase.auth.getSession()
        if (session) {
          await setPasswordResetPending()
        }
        window.history.replaceState({}, '', '/')
      }
    }

    await initApp()

    if (window.location.pathname.includes(RESET_PASSWORD_PATH)) {
      if (new URL(window.location.href).searchParams.has('error')) {
        f7.dialog.alert('Odkaz na obnovenie hesla vypršal. Vyžiadajte si nový.', 'Neplatný odkaz')
      }
      window.history.replaceState({}, '', '/')
    }
  })
})

let tokenValidityInterval: ReturnType<typeof setInterval> | null = null

const startTokenValidityCheck = () => {
  stopTokenValidityCheck()
  tokenValidityInterval = setInterval(async () => {
    const isValid = await checkTokenValidity()
    if (!isValid) stopTokenValidityCheck()
  }, 1800000)
}

const stopTokenValidityCheck = () => {
  if (tokenValidityInterval) {
    clearInterval(tokenValidityInterval)
    tokenValidityInterval = null
  }
}

watch(isCoreAppLoaded, (newValue: boolean | undefined) => {
  if (!newValue) {
    closeAppPreloader ??= showPreloader('Načítavam aplikáciu...')
  } else {
    closeAppPreloader?.(false)
    closeAppPreloader = undefined
  }
})
</script>
